/**
 * Sentry Error Tracking Configuration
 * Captures errors and performance metrics in production
 */

import * as Sentry from '@sentry/react';
import config from '../config';

export function initSentry() {
    // Only initialize in production or if explicitly enabled
    const shouldInit = config.app.env === 'production' || config.debug.enabled;

    if (!shouldInit) {
        console.log('Sentry: Skipped initialization (development mode)');
        return;
    }

    const dsn = import.meta.env.VITE_SENTRY_DSN;

    if (!dsn || dsn === '') {
        console.warn('Sentry: DSN not configured, error tracking disabled');
        return;
    }

    try {
        Sentry.init({
            dsn,
            environment: config.app.env,
            enabled: config.app.env === 'production',

            // Performance Monitoring
            tracesSampleRate: config.app.env === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev

            // Session Replay (optional)
            replaysSessionSampleRate: 0.1, // 10% of sessions
            replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

            // Tracing
            tracePropagationTargets: [
                'localhost',
                config.auth.supabase.url,
                /^\//,
            ],

            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration({
                    maskAllText: true,
                    blockAllMedia: true,
                }),
            ],

            // Filter out errors
            beforeSend(event, hint) {
                // Don't send errors in development
                if (config.app.env === 'development' && !config.debug.enabled) {
                    return null;
                }

                // Filter out known non-critical errors
                const error = hint.originalException;
                if (error && typeof error === 'object' && 'message' in error) {
                    const message = String(error.message).toLowerCase();

                    // Ignore network errors (often user connection issues)
                    if (message.includes('network') || message.includes('fetch')) {
                        return null;
                    }

                    // Ignore ResizeObserver errors (browser quirk)
                    if (message.includes('resizeobserver')) {
                        return null;
                    }
                }

                return event;
            },
        });

        console.log('✅ Sentry initialized:', config.app.env);
    } catch (error) {
        console.error('Failed to initialize Sentry:', error);
    }
}

/**
 * Set user context for error tracking
 */
export function setSentryUser(user: {
    id: string;
    email?: string;
    subscriptionTier?: string;
}) {
    Sentry.setUser({
        id: user.id,
        email: user.email,
        subscription: user.subscriptionTier,
    });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
    Sentry.setUser(null);
}

/**
 * Add breadcrumb for tracking user actions
 */
export function addBreadcrumb(message: string, data?: Record<string, any>) {
    Sentry.addBreadcrumb({
        message,
        level: 'info',
        data,
    });
}

/**
 * Manually capture an error
 */
export function captureError(error: Error, context?: Record<string, any>) {
    Sentry.captureException(error, {
        extra: context,
    });
}

/**
 * Capture a message (for warnings, not errors)
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
    Sentry.captureMessage(message, level);
}
